import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup, usePropertyField } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';

// ============================================================================
// Navigation Structure
// ============================================================================

interface NavChild {
  label: string;
  href: string;
}

interface NavItem {
  label: string;
  href?: string;
  children?: NavChild[];
}

const NAV_ITEMS: NavItem[] = [
  {
    label: 'Find Your Home',
    children: [
      { label: 'Interactive Map', href: '/interactive-map' },
      { label: 'Floor Plans', href: '/floor-plans' },
    ],
  },
  {
    label: 'Features',
    children: [
      { label: 'Amenities', href: '/amenities' },
      { label: 'Pet Policy', href: '/pet-policy' },
    ],
  },
  {
    label: 'Gallery & Tours',
    children: [
      { label: 'Gallery', href: '/gallery' },
      { label: 'Virtual Tours', href: '/virtual-tour' },
    ],
  },
  { label: 'Neighborhood', href: '/neighborhood' },
  { label: 'Contact Us', href: '/contact' },
];

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_mono_white_logo',
      suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_secondary_logo'],
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/apply-now' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule Tour Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// SVG Icons
// ============================================================================

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// ============================================================================
// Sidebar Nav Item (with expandable children)
// ============================================================================

function SidebarNavItem({
  item,
  isOpen,
  onToggle,
}: {
  item: NavItem;
  isOpen: boolean;
  onToggle: () => void;
}) {
  if (!item.children) {
    return (
      <a
        href={item.href}
        className="sidebar-nav-link flex items-center gap-2 px-5 py-2.5 font-body text-[13px] font-semibold tracking-wider uppercase text-white/80 hover:text-[var(--vibe-color-primary)] hover:bg-white/5 transition-all duration-200"
      >
        <ChevronRightIcon className="w-3 h-3 opacity-0 -ml-1 group-hover:opacity-100 transition-opacity" />
        {item.label}
      </a>
    );
  }

  return (
    <div>
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full px-5 py-2.5 font-body text-[13px] font-semibold tracking-wider uppercase text-white/80 hover:text-[var(--vibe-color-primary)] hover:bg-white/5 transition-all duration-200"
        aria-expanded={isOpen}
      >
        <span>{item.label}</span>
        <ChevronDownIcon
          className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        {item.children.map((child) => (
          <a
            key={child.href}
            href={child.href}
            className="block py-2 pl-9 pr-5 font-body text-xs text-white/50 hover:text-[var(--vibe-color-primary)] hover:pl-10 transition-all duration-150"
          >
            {child.label}
          </a>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Main Header Component — Sidebar Layout
// ============================================================================

const SIDEBAR_WIDTH = 260;

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openAccordion, setOpenAccordion] = useState<number | null>(null);

  const phone = useContentToken('header.phone');
  const fullAddress = useContentToken('header.full_address');

  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const monoWhiteLogo = usePropertyField('property_mono_white_logo');
  const primaryLogo = usePropertyField('property_primary_logo');
  const logo = { ...logoRaw, image: monoWhiteLogo || primaryLogo || logoRaw.image };

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);

  const googlePlaceUrl = usePropertyField('property_google_place_url') as string | null;
  const directionsUrl =
    googlePlaceUrl ||
    (fullAddress
      ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(String(fullAddress))}`
      : '#');

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const closeMobileMenu = () => {
    setMenuOpen(false);
    setOpenAccordion(null);
  };

  // Shared sidebar content used in both desktop sidebar and mobile drawer
  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="sidebar-logo-area flex-shrink-0 px-5 pt-6 pb-4 border-b border-white/10">
        <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef}>
          <a href="/" className="block">
            {logo.image ? (
              <img
                src={logo.image}
                alt={logo.alt || 'Community Logo'}
                className="h-10 w-auto object-contain"
              />
            ) : (
              <div className="font-heading text-xl font-bold text-white">
                The Preferred
              </div>
            )}
          </a>
        </EditableSlotGroup>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4" role="navigation" aria-label="Main navigation">
        {NAV_ITEMS.map((item, index) => (
          <SidebarNavItem
            key={item.label}
            item={item}
            isOpen={openAccordion === index}
            onToggle={() =>
              setOpenAccordion(openAccordion === index ? null : index)
            }
          />
        ))}
      </nav>

      {/* Bottom Section: CTA + Utility Links + Contact Info */}
      <div className="flex-shrink-0 border-t border-white/10">
        {/* Schedule Tour CTA */}
        <div className="px-4 pt-4 pb-3">
          <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
            <a
              href={scheduleTourLink.url || '/schedule-a-tour'}
              target={scheduleTourLink._target || '_self'}
              className="sidebar-cta-btn group/cta relative flex items-center justify-center gap-2 w-full py-3 text-[11px] font-bold tracking-[0.15em] uppercase text-white overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">
                {scheduleTourLink.label || 'Schedule a Tour'}
              </span>
              <svg
                className="relative z-10 w-3.5 h-3.5 transition-transform duration-300 group-hover/cta:translate-x-0.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </a>
          </EditableSlotGroup>
        </div>

        {/* Utility Links */}
        <div className="px-4 pb-3 flex gap-2">
          <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
            <a
              href={applyNowLink.url || '/apply-now'}
              target={applyNowLink._target || '_self'}
              className="flex-1 text-center py-2 text-[10px] font-bold tracking-wider uppercase border border-white/20 text-white/80 hover:bg-white/10 hover:border-white/40 transition-all rounded-sm"
            >
              {applyNowLink.label || 'Apply Now'}
            </a>
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
            <a
              href={residentAccessLink.url || '/resident-access'}
              target={residentAccessLink._target || '_self'}
              className="flex-1 text-center py-2 text-[10px] font-bold tracking-wider uppercase border border-white/20 text-white/80 hover:bg-white/10 hover:border-white/40 transition-all rounded-sm"
            >
              {residentAccessLink.label || 'Resident Access'}
            </a>
          </EditableSlotGroup>
        </div>

        {/* Contact Info */}
        <div className="px-5 pb-5 pt-2 space-y-2.5">
          <a
            href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
            className="flex items-center gap-2 text-xs text-white/50 hover:text-[var(--vibe-color-primary)] transition-colors"
          >
            <PhoneIcon className="w-3.5 h-3.5 text-[var(--vibe-color-primary)] flex-shrink-0" />
            <EditableToken
              path="header.phone"
              defaultField="property_phone"
              defaultValue="(555) 555-5555"
              as="span"
            >
              {formatPhone(phone)}
            </EditableToken>
          </a>
          <a
            href={directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-xs text-white/50 hover:text-[var(--vibe-color-primary)] transition-colors"
          >
            <MapPinIcon className="w-3.5 h-3.5 text-[var(--vibe-color-primary)] flex-shrink-0" />
            <EditableToken
              path="header.full_address"
              defaultField="property_full_address"
              defaultValue="123 Main Street, Atlanta, GA 30313"
              as="span"
              className="leading-tight"
            >
              {fullAddress}
            </EditableToken>
          </a>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* ================================================================== */}
      {/* Desktop Sidebar (lg+)                                              */}
      {/* ================================================================== */}
      <aside
        className="sidebar-desktop hidden lg:flex fixed top-0 left-0 bottom-0 z-50 flex-col"
        style={{ width: SIDEBAR_WIDTH }}
      >
        {sidebarContent}
      </aside>

      {/* ================================================================== */}
      {/* Mobile Top Bar (below lg)                                          */}
      {/* ================================================================== */}
      <header className="sidebar-mobile-bar lg:hidden fixed top-0 left-0 right-0 z-50"
        style={{ top: 'var(--offer-banner-height, 0px)' }}
      >
        <div className="flex items-center justify-between h-14 px-4">
          {/* Logo */}
          <a href="/" className="flex-shrink-0">
            {logo.image ? (
              <img
                src={logo.image}
                alt={logo.alt || 'Community Logo'}
                className="h-8 w-auto object-contain"
              />
            ) : (
              <div className="font-heading text-lg font-bold text-white">
                The Preferred
              </div>
            )}
          </a>

          {/* Hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="relative w-10 h-10 flex items-center justify-center"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <span
              className={`absolute block w-6 h-0.5 bg-white transition-all duration-300 ${
                menuOpen ? 'rotate-45' : '-translate-y-2'
              }`}
            />
            <span
              className={`absolute block w-6 h-0.5 bg-white transition-all duration-300 ${
                menuOpen ? 'opacity-0' : ''
              }`}
            />
            <span
              className={`absolute block w-6 h-0.5 bg-white transition-all duration-300 ${
                menuOpen ? '-rotate-45' : 'translate-y-2'
              }`}
            />
          </button>
        </div>
      </header>

      {/* ================================================================== */}
      {/* Mobile Slide-Out Drawer                                            */}
      {/* ================================================================== */}

      {/* Backdrop */}
      <div
        className={`lg:hidden fixed inset-0 bg-black/50 z-[59] transition-opacity duration-300 ${
          menuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        }`}
        onClick={closeMobileMenu}
      />

      {/* Drawer */}
      <aside
        className={`lg:hidden fixed top-0 left-0 bottom-0 z-[60] flex flex-col sidebar-desktop transition-transform duration-300 ease-out ${
          menuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ width: SIDEBAR_WIDTH }}
      >
        {sidebarContent}
      </aside>

      {/* ================================================================== */}
      {/* Global Styles                                                      */}
      {/* ================================================================== */}
      <style>{`
        /* ============================================================ */
        /* Sidebar background                                            */
        /* ============================================================ */
        .sidebar-desktop {
          background: linear-gradient(
            180deg,
            var(--vibe-color-secondary) 0%,
            color-mix(in srgb, var(--vibe-color-secondary) 80%, black) 100%
          );
        }

        /* ============================================================ */
        /* Mobile top bar background                                     */
        /* ============================================================ */
        .sidebar-mobile-bar {
          background: linear-gradient(
            135deg,
            var(--vibe-color-secondary) 0%,
            color-mix(in srgb, var(--vibe-color-secondary) 85%, black) 100%
          );
        }

        /* ============================================================ */
        /* CTA button styling                                            */
        /* ============================================================ */
        .sidebar-cta-btn {
          background: linear-gradient(
            135deg,
            var(--vibe-color-primary) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%
          );
          border-radius: 9999px;
          box-shadow:
            0 1px 3px rgba(0,0,0,0.12),
            0 2px 8px color-mix(in srgb, var(--vibe-color-primary) 25%, transparent);
        }
        .sidebar-cta-btn:hover {
          background: linear-gradient(
            135deg,
            color-mix(in srgb, var(--vibe-color-primary) 90%, white) 0%,
            var(--vibe-color-primary) 100%
          );
          box-shadow:
            0 2px 6px rgba(0,0,0,0.15),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 35%, transparent);
        }

        /* ============================================================ */
        /* Push page content to the right on desktop                      */
        /* ============================================================ */
        @media (min-width: 1024px) {
          .vibesites-layout {
            padding-left: ${SIDEBAR_WIDTH}px;
          }
        }

        /* ============================================================ */
        /* Mobile top bar spacer                                         */
        /* ============================================================ */
        @media (max-width: 1023px) {
          .vibesites-main {
            padding-top: 56px; /* h-14 = 3.5rem = 56px */
          }
        }

        /* ============================================================ */
        /* Scrollbar styling for sidebar nav                             */
        /* ============================================================ */
        .sidebar-desktop nav::-webkit-scrollbar {
          width: 4px;
        }
        .sidebar-desktop nav::-webkit-scrollbar-track {
          background: transparent;
        }
        .sidebar-desktop nav::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.15);
          border-radius: 2px;
        }
        .sidebar-desktop nav::-webkit-scrollbar-thumb:hover {
          background: rgba(255,255,255,0.25);
        }
      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Sidebar Navigation',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_phone',
      defaultValue: '(555) 555-5555',
    },
    'header.full_address': {
      label: 'Full Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
  },
  slotGroups: {
    'header.logo': logoSlotDef,
    'header.apply_now_link': applyNowLinkDef,
    'header.resident_access_link': residentAccessLinkDef,
    'header.schedule_tour_link': scheduleTourLinkDef,
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
